import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import * as d3 from "d3";
import tippy from "tippy.js";
import { TippyContent } from "../../../../components/GanttTimeLine/Timeline/ChartCell/TippyContent";

const Chart = ({ chartWidth, chartHeight, eTime, cData }) => {
  const svgRef = useRef(null);

  function renderReactComponentToDiv(component) {
    const container = document.createElement("div");
    ReactDOM.render(component, container);
    return container;
  }

  function createTooltip(element, data) {
    const tooltipContent = renderReactComponentToDiv(
      <TippyContent job={data} />,
    );

    //? This is Being saved due to possible future modifications to be made on the Charts Tippy!
    const backgroundColor = data.status === "done" ? "#111" : "#111";

    tippy(element, {
      content: tooltipContent,
      allowHTML: true,
      interactive: true,
      arrow: true,
      duration: 200,
      placement: "top",
      delay: [200, 0],
      appendTo: document.querySelector("#zaivu-burndown-chart"),
      onShow(instance) {
        const tooltipBox = instance.popper.querySelector(".tippy-box");
        if (tooltipBox) {
          tooltipBox.style.backgroundColor = backgroundColor;
        }
        if (instance.props.arrow) {
          instance.popper.querySelector(".tippy-arrow").style.color =
            backgroundColor;
        }
      },
    });
  }

  function getColor(x, y, eTime) {
    let hue = 60;
    if (x + y >= eTime * 1.5) {
      hue = 0;
    } else if (x + y <= eTime * 0.5) {
      hue = 120;
    } else {
      hue = 60 + ((eTime - (x + y)) * 120) / eTime;
    }
    return `hsla(${hue}, 100%, 50%, 1)`;
  }
  function getDarkerColor(x, y, eTime) {
    let hue = 60;
    if (x + y >= eTime * 1.5) {
      hue = 0;
    } else if (x + y <= eTime * 0.5) {
      hue = 120;
    } else {
      hue = 60 + ((eTime - (x + y)) * 120) / eTime;
    }
    return `hsla(${hue}, 100%, 30%, 1)`;
  }

  useEffect(() => {
    // limpar dados anteriores
    d3.select(svgRef.current).selectAll("*").remove();

    // const data1 = cData;
    const data1 = [{ x: 0, y: eTime }];

    for (const task of cData) {
      data1.push({
        x: task.x,
        y: task.y,
        status: task.status,
      });
    }

    const data2 = [
      { x: 0, y: eTime },
      { x: eTime, y: 0 },
    ];

    const svgWidth = chartWidth;
    const svgHeight = chartHeight;
    const margin = { top: 20, right: 20, bottom: 45, left: 40 };
    const width = svgWidth - margin.right - margin.left;
    const height = svgHeight - margin.top - margin.bottom;

    const svg = d3
      .select(svgRef.current)
      .attr("width", svgWidth)
      .attr("height", svgHeight)
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    const xScale = d3
      .scaleLinear()
      .domain([0, Math.max(eTime, data1[data1.length - 1].x)])
      .range([0, width]);
    const yScale = d3.scaleLinear().domain([0, eTime]).range([height, 0]);

    const lineGenerator1 = d3
      .line()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));
    const lineGenerator2 = d3
      .line()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));

    //? Area Function

    const area = d3
      .area()
      .x((d) => xScale(d.x))
      .y0(yScale(0)) // start at y=0
      .y1((d) => yScale(d.y)); // up to the line

    svg
      .append("path")
      .datum(data1)
      .attr("fill", "url(#paint0_linear_1296_23759)")
      .attr("d", area);

    //? Line Generators

    svg
      .append("path")
      .data([data2])
      .attr("d", lineGenerator2)
      .attr("fill", "none")
      .attr("stroke", "#A9AEB4")
      .attr("stroke-width", "2px");

    //! Original Real Line Generator
    /*  svg
      .append("path")
      .data([data1])
      .attr("d", lineGenerator1)
      .attr("fill", "none")
      .attr("stroke", "#40E5A0")
      .attr("stroke-width", "3.53px");
*/
    //? New Real LIne Generator Test
    // Define gradients for each segment
    for (let i = 0; i < data1.length - 1; i++) {
      const startPoint = data1[i];
      const endPoint = data1[i + 1];

      // Colors at start and end of the segment
      const startColor = getColor(startPoint.x, startPoint.y, eTime);
      const endColor = getColor(endPoint.x, endPoint.y, eTime);

      // Define the gradient
      const gradientId = `gradient-${i}`;
      const defs = svg.append("defs");
      const gradient = defs
        .append("linearGradient")
        .attr("id", gradientId)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", xScale(startPoint.x))
        .attr("y1", yScale(startPoint.y))
        .attr("x2", xScale(endPoint.x))
        .attr("y2", yScale(endPoint.y));

      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", startColor);

      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", endColor);

      // Draw the segment with the gradient
      svg
        .append("line")
        .attr("x1", xScale(startPoint.x))
        .attr("y1", yScale(startPoint.y))
        .attr("x2", xScale(endPoint.x))
        .attr("y2", yScale(endPoint.y))
        .attr("stroke", `url(#${gradientId})`)
        .attr("stroke-width", "3.53px");
    }

    svg
      // Generates de X Axis
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(6))
      .selectAll(".domain, .tick line") // This selects the main axis line (domain) and the tick lines
      .attr("stroke-opacity", 0); // Setting the opacity to 0 will hide them

    svg
      // Generates the Y Axis
      .append("g")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(6)
          .tickFormat((d) => (d === 0 ? "" : d)),
      )
      .selectAll(".domain, .tick line")
      .attr("stroke-opacity", 0);

    svg
      .selectAll(".tick text")
      .style("fill", "#7E858F")
      .style("font-family", "Sora")
      .style("font-size", "14.122px")
      .style("font-style", "normal")
      .style("font-weight", "400")
      .style("line-height", "normal");

    //? Dots Block
    //? White Dots

    const wDLGradient = svg
      .append("defs") // White Dots Linear Gradient
      .append("linearGradient")
      .attr("id", "paint0_linear_1296_23755")
      .attr("x1", "9.98063")
      .attr("y1", "6.51343")
      .attr("x2", "9.98063")
      .attr("y2", "14.9864")
      .attr("gradientUnits", "userSpaceOnUse");

    wDLGradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#EFF1F5");

    wDLGradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "white");

    // Radial Gradient
    const wDRGRadient = svg
      .append("defs") // White Dots Radial Gradient
      .append("radialGradient")
      .attr("id", "paint1_radial_1296_23755")
      .attr("gradientUnits", "objectBoundingBox") // Adjusted gradient units
      .attr("cx", "0.5") // Centered at 50% of the bounding box width
      .attr("cy", "0.5") // Centered at 50% of the bounding box height
      .attr("r", "0.5"); // Radius set to half the bounding box's dimension

    wDRGRadient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#EFF1F5");

    wDRGRadient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#EFF1F5")
      .attr("stop-opacity", "0.12");

    // White Dots Ploter
    data2.forEach((point) => {
      svg
        .append("circle")
        .attr("cx", xScale(point.x))
        .attr("cy", yScale(point.y))
        .attr("r", 4.23649)
        .attr("fill", "url(#paint0_linear_1296_23755)");

      svg
        .append("circle")
        .attr("cx", xScale(point.x))
        .attr("cy", yScale(point.y))
        .attr("r", 9.88513)
        .attr("fill", "url(#paint1_radial_1296_23755)");

      for (let i = 1; i < data1.length; i++) {
        let point = data1[i];
        // console.log("data", i, " :", data1[i]);
        const currentColor = getColor(point.x, point.y, eTime);
        const darkerColor = getDarkerColor(point.x, point.y, eTime);

        const uniqueRadialGradientId = "paint0_radial_" + i;
        const uniqueLinearGradientId = "paint1_linear_" + i;

        // Radial Gradient Definition
        const gDRGradient = svg
          .append("defs")
          .append("radialGradient")
          .attr("id", uniqueRadialGradientId)
          .attr("gradientUnits", "objectBoundingBox")
          .attr("cx", "0.5")
          .attr("cy", "0.5")
          .attr("r", "0.5");

        gDRGradient
          .append("stop")
          .attr("offset", "0%")
          .attr("stop-color", currentColor);

        gDRGradient
          .append("stop")
          .attr("offset", "100%")
          .attr("stop-color", currentColor)
          .attr("stop-opacity", "0.12");

        // Linear Gradient Definition
        const gDLGradient = svg
          .append("defs")
          .append("linearGradient")
          .attr("id", uniqueLinearGradientId)
          .attr("x1", "0")
          .attr("y1", "0")
          .attr("x2", "0.5")
          .attr("y2", "0.5")
          .attr("gradientUnits", "objectBoundingBox");

        gDLGradient
          .append("stop")
          .attr("offset", "0%")
          .attr("stop-color", currentColor);

        gDLGradient
          .append("stop")
          .attr("offset", "100%")
          .attr("stop-color", darkerColor); // You can modify this if needed

        if (point.status === "done" || point.status === "doneLate") {
          const finalDot = svg
            .append("g")
            .attr(
              "transform",
              `translate(${xScale(point.x)}, ${yScale(point.y)})`,
            )
            .on("mouseover", function () {
              outerCircle.attr("r", 9.88513 * 2); // Increasing the radius by 100%
              innerCircle.attr("r", 4.23649 * 2);
              d3.select(this).style("cursor", "pointer");
            })
            .on("mouseout", function () {
              outerCircle.attr("r", 9.88513);
              innerCircle.attr("r", 4.23649);
              d3.select(this).style("cursor", "default");
            });

          // Larger circle with radial gradient
          const outerCircle = finalDot
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", 9.88513)
            .attr("fill", "url(#" + uniqueRadialGradientId + ")")
            .style("transition", "r 1s");

          // Inner circle with linear gradient
          const innerCircle = finalDot
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", 4.23649)
            .attr("fill", "url(#" + uniqueLinearGradientId + ")")
            .style("transition", "r 0.5s");

          // Attach the tooltip to the group
          createTooltip(finalDot.node(), cData[i - 1]);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartWidth, chartHeight]);

  return <svg ref={svgRef}></svg>;
};

export default Chart;
